import { ChevronDown } from 'lucide-react';
import { createContext, useContext, useState, useEffect, Children, isValidElement, cloneElement, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useToken } from '../../hooks/useToken';
import { api } from '../../lib/api';
import { UpsellCard } from '../cards/UpsellCard';
import FooterSidebar from './FooterSidebar';
import PortalSelectorDropdown from './PortalSelectorDropdown';

const SidebarContext = createContext();

const Sidebar = ({ children, expanded, moreItems }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 766);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const sidebarRef = useRef(null);
    const [userRolesState] = useState([]);
    const { user } = useUser();
    const token = useToken();

    const { data: userRoles = userRolesState } = useQuery({
        queryKey: ['user', 'roles', user?.id],
        queryFn: () => api.getUserRoles(user?.id, token || ''),
        enabled: !!token && !!user?.id,
        select: d => d.roles ?? d,
    });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            const mobile = width <= 766;
            setIsMobile(mobile);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const visibleChildren = Children.toArray(children).filter(child => {
        if (isValidElement(child) && child.props.children && isValidElement(child.props.children)) {
            const { desktopOnly, mobileOnly } = child.props.children.props;
            if (isMobile && desktopOnly) return false;
            if (!isMobile && mobileOnly) return false;
        }
        return true;
    });

    const hasAdminAccess = (userRoles || []).length > 0;

    return (
        <>
            {isDropdownOpen && <div className="fixed inset-0 bg-neutral-950/10 backdrop-blur-xs z-40" />}
            {/* Sidebar for large screens */}
            <aside
                className="hidden fixed md:flex shrink-0 z-40 h-screen bg-neutral-100 backdrop-blur-xs border-r border-neutral-300 overflow-hidden transition-all duration-200"
                ref={sidebarRef}
            >
                <nav className="h-full flex flex-col min-h-0 overflow-hidden">
                    {expanded && (
                        <div className="p-4 pb-2 relative">
                            <div
                                className={`overflow-hidden transition-all flex flex-row w-60 justify-between ${
                                    expanded ? 'w-53' : 'w-0'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <img src="/Zindua-Alt-Favicon.png" alt="Zindua LMS favicon" className="w-10 h-10" />
                                    <div
                                        className={`flex flex-col gap-1 transition-opacity duration-300 ${
                                            expanded ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                        }`}
                                    >
                                        <h5 className="text-neutral-950 text-h5">Zindua LMS</h5>
                                        <p className="text-neutral-500 text-[13px] font-normal line-clamp-1">
                                            Student Application
                                        </p>
                                    </div>
                                </div>
                                <PortalSelectorDropdown
                                    isDropdownOpen={isDropdownOpen}
                                    setIsDropdownOpen={setIsDropdownOpen}
                                    hasAdminAccess={hasAdminAccess}
                                />
                            </div>
                        </div>
                    )}
                    {!expanded && (
                        <div className="flex justify-center items-center p-4 pb-2">
                            <img src="/Zindua-Alt-Favicon.png" alt="Zindua LMS favicon" className="w-10 p-1" />
                        </div>
                    )}
                    <SidebarContext.Provider value={{ expanded, isMobile }}>
                        <ul className={`flex-1 py-4 overflow-y-auto flex-col mx-2 hidden md:flex`}>
                            {visibleChildren}
                            <SidebarItem text="More" dropdown persistKey="sidebar-more-open" dropdownItems={moreItems} />
                            {expanded && (
                                <div className="mt-auto px-4 pt-4 pb-1 flex flex-col gap-4">
                                    <UpsellCard
                                        title="Join our immersive coding programs"
                                        isDiscounted={false}
                                        description="Upskill into Software Eng. or Data Science. For advanced devs, you can specialize in AI, DevOps and/or Data Eng."
                                        link="https://zinduaschool.com#programs"
                                    />
                                    <FooterSidebar />
                                </div>
                            )}
                        </ul>
                    </SidebarContext.Provider>
                </nav>
            </aside>

            {/* Bottom navigation for small screens */}
            <div className="bottom-nav-blur md:hidden fixed bottom-0 left-0 right-0 bg-neutral-50 border-t border-neutral-300 z-50 transition-all duration-200">
                <nav>
                    <SidebarContext.Provider value={{ expanded, isMobile }}>
                        <ul className="flex justify-around py-2">{visibleChildren}</ul>
                    </SidebarContext.Provider>
                </nav>
            </div>
        </>
    );
};

const SidebarItem = ({
    icon,
    text,
    mobileText,
    active,
    dropdown,
    desktopOnly,
    mobileOnly,
    href,
    dropdownItems = [],
    persistKey,
}) => {
    const { expanded, isMobile } = useContext(SidebarContext);
    const [open, setOpen] = useState(() => {
        if (persistKey) {
            const saved = localStorage.getItem(persistKey);
            return saved ? JSON.parse(saved) : false;
        }
        return false;
    });

    useEffect(() => {
        if (persistKey) {
            localStorage.setItem(persistKey, JSON.stringify(open));
        }
    }, [open, persistKey]);

    if (isMobile && desktopOnly) return null;
    if (!isMobile && mobileOnly) return null;

    // Mobile view
    if (isMobile) {
        return (
            <li
                className={`flex flex-col items-center cursor-pointer transition-colors ${
                    active ? 'text-primary' : 'text-neutral-950'
                } `}
            >
                {icon}
                <span className="text-xs">{mobileText || text}</span>
            </li>
        );
    }

    // Desktop view - collapsed sidebar with dropdown
    if (!expanded && dropdown) {
        return (
            <>
                <li
                    onClick={() => setOpen(open => !open)}
                    className={`relative group z-40 flex items-center w-full justify-center px-6 py-2 cursor-pointer hover:bg-neutral-200 hover:text-primary transition-colors ml-0 rounded-lg ${
                        active ? 'bg-neutral-200 text-primary' : 'text-neutral-700'
                    }`}
                >
                    <ChevronDown className={`transition-transform w-5 h-5 ${open ? 'rotate-180' : ''}`} />
                </li>
                {open && (
                    <ul className="flex flex-col w-full">
                        {dropdownItems.map((item, index) => (
                            <li
                                key={`dropdown-collapsed-${index}`}
                                onClick={item.onClick}
                                className={`relative group w-full z-40 flex items-center pl-6 py-2.5 cursor-pointer hover:bg-neutral-200 hover:text-primary transition-colors ml-0 rounded-lg ${
                                    item.active ? 'bg-neutral-200 text-primary' : 'text-neutral-700'
                                }`}
                            >
                                <div className="transition-all">
                                    {isValidElement(item.icon)
                                        ? cloneElement(item.icon, {
                                              className: `w-5 h-5 ${item.active ? 'text-primary' : 'text-neutral-950'}`,
                                          })
                                        : item.icon}
                                </div>
                                <div
                                    className={`absolute border left-full whitespace-nowrap px-2 py-1 bg-neutral-200 rounded-md ml-3 invisible opacity-20 -translate-x-3 transition-all group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 text-sm`}
                                >
                                    {item.label}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </>
        );
    }

    // Desktop view - expanded sidebar
    return (
        <>
            <li
                onClick={() => {
                    if (expanded && dropdown) setOpen(open => !open);
                    else if (href) window.open(href, '_self');
                }}
                className={`relative group z-40 flex items-center w-full px-6 py-2 cursor-pointer hover:bg-neutral-200 hover:text-primary transition-colors ml-0 rounded-lg ${
                    active ? 'bg-neutral-200 text-primary' : 'text-neutral-700'
                }`}
            >
                <div className="transition-all">{icon}</div>
                <p
                    className={`overflow-hidden flex items-center gap-2 whitespace-nowrap transition-all hover:text-primary ${
                        expanded ? `w-40 ${!dropdown ? 'ml-3' : ''}` : 'w-0'
                    } ${active ? ' text-primary' : 'text-neutral-700 group-hover:text-primary'}`}
                >
                    {dropdown && <ChevronDown className={`transition-transform ${open ? 'rotate-180' : ''}`} />}
                    {text}
                </p>

                {!expanded && !dropdown && (
                    <div
                        className={`absolute border left-full whitespace-nowrap px-2 py-1 bg-neutral-200 rounded-md ml-3 invisible opacity-20 -translate-x-3 transition-all group-hover:visible group-hover:opacity-100 group-hover:translate-x-0`}
                    >
                        {text}
                    </div>
                )}
            </li>
            {dropdown && expanded && (
                <ul className={`transition-all${open ? 'h-auto' : 'hidden'}`}>
                    {open &&
                        dropdownItems &&
                        dropdownItems.map((item, idx) => (
                            <div key={`dropdown-item-${idx}`}>
                                {idx === 2 && <div className="border-t border-neutral-300 mx-4 my-1" />}
                                <li
                                    className={`flex items-center pl-6 py-0.5 cursor-pointer transition-colors ml-1 rounded-lg`}
                                    onClick={item.onClick}
                                >
                                    <span
                                        className={`text-[1rem] px-3 py-1.5 rounded-lg transition-colors flex-1 ${
                                            item.active
                                                ? 'bg-neutral-200 text-primary'
                                                : 'text-neutral-500 hover:bg-neutral-200 hover:text-primary'
                                        }`}
                                    >
                                        {item.label}
                                    </span>
                                </li>
                            </div>
                        ))}
                </ul>
            )}
        </>
    );
};

export { SidebarItem };
export default Sidebar;
